import React from 'react';

import { Stack } from '@/components/layout';
import { Text } from '@/components/ui';
import { Collapsible } from '@/components/ui/collapsible/collapsible';
import { STRINGS } from '@/constants/strings';
import type { PlayerProgressBadge } from '@/features/player/components/types';
import {
  formatProgressBadgeTitle,
  formatProgressCheckpointTitleOverview,
} from '@/features/player/utils/progress-title';
import { useScaling } from '@/hooks';

import {
  CHECKPOINT_LEADER_DOTS,
  formatDateEarnedShort,
  getSkillHeaderStatusLabel,
  isCheckpointStatusCompleted,
  sortProgressCheckpoints,
  uniqueAttemptDisplayDatesNewestFirst,
} from './progress-tracker.utils';
import { useStyles } from './styles';

export function CurrentLevelBadgeRow({
  badge,
  levelColor,
  isLast,
  context: _context = 'default',
}: {
  badge: PlayerProgressBadge;
  levelColor: string;
  isLast: boolean;
  context?: 'default' | 'levelDetails';
}) {
  const styles = useStyles();
  const { isTablet, scale } = useScaling();
  const copy = STRINGS.player.profile.progressTrackerContent;
  const badgeTitle = formatProgressBadgeTitle(badge.name);

  const checkpoints = React.useMemo(
    () => sortProgressCheckpoints(badge.checkpoints),
    [badge.checkpoints],
  );

  const badgeStatusText = React.useMemo(
    () =>
      getSkillHeaderStatusLabel(badge, checkpoints, copy.completedOnLabel, copy.statusNotComplete),
    [badge, checkpoints, copy.completedOnLabel, copy.statusNotComplete],
  );

  return (
    <Stack>
      <Collapsible defaultOpen={false} containerStyle={styles.collapsibleContainer}>
        <Collapsible.Trigger triggerStyle={styles.badgeTrigger}>
          <Stack
            direction="row"
            align="center"
            justify="space-between"
            style={styles.badgeTriggerInner}>
            <Text
              variant={'Font Inter/Subtitle/Medium 14px'}
              style={styles.badgeName}
              numberOfLines={1}>
              {badgeTitle}
            </Text>
            <Stack direction="row" align="center" gap={scale(6)}>
              <Text
                variant={
                  isTablet
                    ? 'Font Inter/Subtitle/Regular 11px'
                    : 'Font Montserrat/Subtitle/Minimize 2 Medium 10px'
                }
                style={styles.detailsText}>
                {copy.details}
              </Text>
            </Stack>
          </Stack>
        </Collapsible.Trigger>
        <Collapsible.Content contentStyle={styles.badgeContent}>
          <Stack
            direction="row"
            align="center"
            justify="space-between"
            style={styles.badgeContentHeader}>
            <Text
              variant={'Font Inter/Subtitle/Bold 10px'}
              style={[styles.badgeContentTitle, { color: levelColor }]}>
              {badgeTitle}
            </Text>
            <Text
              numberOfLines={1}
              ellipsizeMode="clip"
              variant={'Font Inter/Subtitle/Bold 10px'}
              style={[styles.checkpointLeaderText, { color: levelColor }]}>
              {CHECKPOINT_LEADER_DOTS}
            </Text>
            <Text
              variant={'Font Inter/Subtitle/Bold 10px'}
              style={[styles.badgeContentStatus, { color: levelColor }]}>
              {badgeStatusText}
            </Text>
          </Stack>

          <Stack gap={scale(6)}>
            {checkpoints.map((cp, idx) => {
              const checkpointDone = isCheckpointStatusCompleted(cp.status);
              const attempts = Array.isArray(cp.attempts) ? cp.attempts : [];
              const lastAttempt =
                attempts.length > 0 ? String(attempts[attempts.length - 1] ?? '') : '';
              const lastDateShort = formatDateEarnedShort(lastAttempt);
              const checkpointStatusLabel = checkpointDone
                ? lastDateShort
                  ? `${copy.completedOnLabel} ${lastDateShort}`
                  : copy.completed
                : copy.statusNotComplete;

              const incompleteAttemptDates = uniqueAttemptDisplayDatesNewestFirst(
                checkpointDone ? attempts.slice(0, -1) : attempts,
              );

              return (
                <Stack key={`${cp.name}-${idx}`} gap={scale(2)}>
                  <Stack direction="row" align="center" style={styles.checkpointRow}>
                    <Text style={styles.checkpointName} variant={'Font Inter/Subtitle/Regular'}>
                      {formatProgressCheckpointTitleOverview(cp.name)}
                    </Text>
                    <Text
                      numberOfLines={1}
                      ellipsizeMode="clip"
                      variant={
                        isTablet
                          ? 'Font Inter/Subtitle/Regular 12px'
                          : 'Font Inter/Subtitle/Regular'
                      }
                      style={styles.checkpointLeaderText}>
                      {CHECKPOINT_LEADER_DOTS}
                    </Text>
                    <Stack style={styles.checkpointStatusMask}>
                      <Text
                        variant={'Font Inter/Subtitle/Regular'}
                        style={styles.checkpointStatus}
                        numberOfLines={1}>
                        {checkpointStatusLabel}
                      </Text>
                    </Stack>
                  </Stack>

                  {incompleteAttemptDates.length > 0 ? (
                    <Stack paddingLeft={scale(6)} gap={scale(2)}>
                      {incompleteAttemptDates.map((dateShort, j) => (
                        <Text
                          key={`${cp.name}-attempt-${dateShort}-${j}`}
                          fontStyle="italic"
                          variant={'Font Inter/Subtitle/Regular'}
                          style={styles.checkpointStatus}>
                          {`${copy.incompleteAttemptOnLabel} ${dateShort}`}
                        </Text>
                      ))}
                    </Stack>
                  ) : null}
                </Stack>
              );
            })}
          </Stack>
        </Collapsible.Content>
      </Collapsible>
      {isLast ? null : <Stack style={styles.divider} />}
    </Stack>
  );
}
